/**
 * 判断颜色是否为浅色（用于自动切换文字颜色）
 * 使用 W3C 相对亮度公式
 * @param {string} hex - 十六进制颜色值，如 '#ffffff' 或 '#000'
 * @returns {boolean} true = 浅色背景，应使用深色文字
 */
export function isLightColor(hex) {
  if (!hex || typeof hex !== 'string') return false
  // 移除 # 前缀
  const h = hex.replace('#', '')
  // 处理简写形式 #fff
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  if (full.length !== 6) return false

  const r = parseInt(full.substring(0, 2), 16) / 255
  const g = parseInt(full.substring(2, 4), 16) / 255
  const b = parseInt(full.substring(4, 6), 16) / 255

  // sRGB 线性化
  const linearize = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  const luminance = 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b)

  return luminance > 0.5
}

/**
 * 返回浅/深背景对应的 Tailwind 主题 class 映射
 * @param {boolean} isLight - 是否为浅色背景
 * @param {string|null} fontColor - 用户自定义全局字体颜色，null/默认表示自动适配
 * @returns {object} 包含 text, textSecondary, cardBg, cardBorder 等 key
 */
export function getTheme(isLight, fontColor) {
  const useCustomFontColor = fontColor && fontColor !== '#ffffff' && fontColor !== '#000000'
  const base = {
    // 标题/主要文字
    text: useCustomFontColor ? 'text-font-color' : (isLight ? 'text-gray-900' : 'text-white'),
    // 副标题/描述文字
    textSecondary: useCustomFontColor ? 'text-font-color/70' : (isLight ? 'text-gray-600' : 'text-gray-400'),
    // 弱化文字
    textMuted: useCustomFontColor ? 'text-font-color/60' : (isLight ? 'text-gray-500' : 'text-gray-500'),
    // 卡片背景
    cardBg: isLight ? 'bg-white' : 'bg-gray-900',
    // 卡片边框
    cardBorder: isLight ? 'border-gray-200' : 'border-gray-800',
    // 卡片 hover 边框
    cardBorderHover: isLight ? 'hover:border-accent/50' : 'hover:border-accent/50',
    // 次级区块背景
    sectionBg: isLight ? 'bg-gray-50' : 'bg-gray-900/50',
    // 小块背景（按钮/pill等）
    chipBg: isLight ? 'bg-gray-100' : 'bg-gray-800',
    // 小块文字
    chipText: isLight ? 'text-gray-700' : 'text-gray-300',
    // 输入框背景
    inputBg: isLight ? 'bg-gray-50' : 'bg-gray-800',
    // 输入框边框
    inputBorder: isLight ? 'border-gray-300' : 'border-gray-700',
    // 渐变块背景
    gradientBg: isLight
      ? 'bg-gradient-to-light'
      : 'bg-gradient-to-light',
    // 渐变块边框
    gradientBorder: isLight ? 'border-accent/30' : 'border-accent/20',
    // 分隔线
    divider: isLight ? 'border-gray-200' : 'border-gray-800',
    // Footer 边框
    footerBorder: isLight ? 'border-gray-300' : 'border-gray-800',
  }
  return base
}
